<script>
import {
  ChartComponent,
  SeriesDirective,
  SeriesCollectionDirective,
  ColumnSeries,
  Category,
  DataLabel,
  Tooltip,
  Legend,
  Highlight,
} from '@syncfusion/ej2-vue-charts';

import { GROUP_BY_OPTIONS } from 'dashboard/routes/dashboard/settings/reports/constants.js';
import { formatTime } from '@chatwoot/utils';

import {
  messagesMetricColors,
  conversationSubmetrics,
  // COLUMN CHART RESPONSIBLE OPTIONS
  smallest_Sizes,
  smaller_1_Sizes,
  smaller_2_Sizes,
  medium_1_Sizes,
  medium_2_Sizes,
  biggerSizes,
} from '../../../../../../../../../shared/helpers/CustomDashboardsHelper.js';

// POSSIBILIDADES DE DADOS PARA MÉTRICA DE CONVERSAS
const TRANSLATION_KEYS = {
  conversations_count: 'CONVERSATIONS',
  avg_first_response_time: 'FIRST_RESPONSE_TIME',
  avg_resolution_time: 'RESOLUTION_TIME',
  resolutions_count: 'RESOLUTION_COUNT',
  // REPLY_TIME: 'reply_time',
};

export default {
  components: {
    ChartComponent,
    SeriesCollectionDirective,
    SeriesDirective,
  },
  provide: {
    chart: [ColumnSeries, Legend, DataLabel, Category, Tooltip, Highlight],
  },
  props: {
    // Current cell size
    sizeX: {
      type: Number,
      required: true,
    },
    sizeY: {
      type: Number,
      required: true,
    },
    currentWidth: {
      type: Number,
      required: true,
    },
    groupBy: {
      type: Object,
      required: true,
    },
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      totalColor: messagesMetricColors.total,

      theme: 'Material',
      chartArea: { border: { width: 0 } },

      marker: {
        dataLabel: {
          visible: false, // Label com valor da coluna no topo da barra
          position: 'Top',
          font: { fontWeight: '600', color: '#ffffff' },
        },
      },
      legend: { enableHighlight: true },
      tooltip: {
        enable: true,
        // eslint-disable-next-line no-template-curly-in-string
        header: '<b>${point.x}</b>',
        // eslint-disable-next-line no-template-curly-in-string
        format: '${point.y}',
        // shared: true,
      },
      // title: 'GRÁFICO DE COLUNAS',
    };
  },
  computed: {
    maximumY() {
      if (!this.timeSubmetric)
        return Math.max(...this.seriesData.map(obj => obj.value));

      const biggestTimeInSeconds = Math.max(
        ...this.seriesDataForTimeSubmetrics.map(obj => obj.seconds)
      );
      return this.formattedTimeInBiggestRepresentation(biggestTimeInSeconds);
    },
    intervalY() {
      const scales = [
        1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000,
        50000, 100000, 300000,
      ];
      return (
        scales.find(scale => this.maximumY / scale <= 4) ||
        Math.ceil(this.maximumY / 4)
      );
    },
    primaryXAxis() {
      return {
        valueType: 'Category',
        interval: 1,
        majorGridLines: { width: 0 },
        majorTickLines: { width: 0 },
        labelIntersectAction: 'Rotate45',
        labelRotation: 0,

        labelStyle: {
          size: this.responsiveSizes.xAxisLabelStyle,
          // color: 'blue',
          // fontFamily: 'Segoe UI',
          // fontWeight: 'bold',
        },
      };
    },
    // Initializing Primary Y Axis
    primaryYAxis() {
      const { i18nKey } = conversationSubmetrics.find(
        e => e.type === this.data.submetric
      ).title;
      const title = this.$t(i18nKey);

      return {
        title: title,
        maximum: this.maximumY,
        interval: this.intervalY,
        majorTickLines: { width: 0 },
        lineStyle: { width: 0 },
        labelFormat: this.timeSubmetric
          ? `{value}${this.currentTimeRepresentation}`
          : '{value}',
        titleStyle: {
          size: this.responsiveSizes.yAxisLabelStyle,
          fontFamily: 'var($base-font-family)',
          // textAlignment: 'center', // Alinha o texto à esquerda
        },
      };
    },
    timeSubmetric() {
      return (
        this.data.submetric === 'avg_resolution_time' ||
        this.data.submetric === 'avg_first_response_time'
      );
    },
    // Essa Função procura o maior valor que vai ter no gráfico e acha qual a sua grandeza
    // Se estiver dentro da casa dos minutos, o gráfico inteiro será ajustado para esse padrão
    currentTimeRepresentation() {
      const { data } = this.data;
      const biggestTimeObj = data.reduce(
        (max, obj) => (obj.value > max.value ? obj : max),
        data[0]
      );
      const currentTimeRep = formatTime(biggestTimeObj.value).split(' ')[1];
      if (currentTimeRep === 'Sec') return 's'; // SEGUNDOS
      if (currentTimeRep === 'Min') return 'min'; // MINUTOS
      if (currentTimeRep === 'Hr') return 'h'; // HORAS
      if (currentTimeRep === 'Day') return 'd'; // DIAS
      return '{value}';
    },
    seriesData() {
      const report = this.data;
      const seriesList = report.data.map(e => {
        const dateAsString = this.generateDateString(e.timestamp);
        return { date: dateAsString, value: e.value };
      });
      return seriesList;
    },
    seriesDataForTimeSubmetrics() {
      const report = this.data;
      const seriesList = report.data.map(e => {
        const dateAsString = this.generateDateString(e.timestamp);
        return {
          date: dateAsString,
          value: formatTime(e.value),
          seconds: e.value,
          count: e.count,
          time: this.formattedTimeInBiggestRepresentation(e.value),
        };
      });
      return seriesList;
    },
    noDataFound() {
      const anyDateDifferentOfZero =
        this.seriesData.some(date => date.value > 0) ||
        this.seriesDataForTimeSubmetrics.some(date => date.value > 0);
      return !anyDateDifferentOfZero;
    },
    groupByI18nKey() {
      const option = this.groupBy?.period.toUpperCase();
      return option ? GROUP_BY_OPTIONS[option]?.translationKey || '' : '';
    },
    responsiveSizes() {
      // FULL HD (1920x1080)
      if (window.innerWidth >= 1750) {
        if (this.sizeY > 1) {
          if (this.currentWidth < 600) return medium_1_Sizes;
          if (this.currentWidth < 1100) return medium_2_Sizes;
          return biggerSizes;
        }
        return smaller_2_Sizes;
      }
      // NOTEBOOKS (1600 X 900)
      if (window.innerWidth >= 1400) {
        if (this.sizeY > 1) {
          if (this.currentWidth < 550) return smaller_2_Sizes;
          return medium_2_Sizes;
        }
        return smaller_1_Sizes;
      }

      if (this.sizeY > 1) {
        if (this.currentWidth < 250) {
          const sizes = { ...smallest_Sizes, containerPaddingTop: '80' };
          return sizes;
        }
        if (this.currentWidth < 400) return smaller_2_Sizes;
        return medium_1_Sizes;
      }
      return smallest_Sizes;
    },
    currentLanguage() {
      let currentLanguage = this.$root.$i18n.locale.trim();
      if (currentLanguage === 'pt_BR') currentLanguage = 'pt-BR';
      else if (currentLanguage === 'es') currentLanguage = 'es-ES';
      else if (!['es', 'en'].includes(currentLanguage)) currentLanguage = 'en';
      return currentLanguage;
    },
  },
  mounted() {},
  methods: {
    // Transforming unixTimeStamp to readable time
    formatUnixToDate(unixTimestamp, rangeRepresentation = 'dayAndMonth') {
      const isMonth = rangeRepresentation === 'month';
      const isYear = rangeRepresentation === 'year';
      // Jul - 18
      if (!(isMonth || isYear)) {
        const currentMonth = this.localDateString(unixTimestamp, {
          month: 'short',
        });
        const currentDay = this.localDateString(unixTimestamp, {
          day: '2-digit',
        });
        return `${currentDay}-${currentMonth}`;
      }
      let representationParams;
      if (isMonth)
        representationParams = { month: 'short' }; // Jul
      else if (isYear) representationParams = { year: 'numeric' }; // 2024

      return this.localDateString(unixTimestamp, representationParams);
    },
    localDateString(unixTimestamp, representationParams) {
      return new Date(unixTimestamp * 1000).toLocaleDateString(
        this.currentLanguage,
        representationParams
      );
    },

    generateDateString(unixTimeStamp) {
      const sixDaysLater = timestamp => timestamp + 6 * 24 * 60 * 60;
      const { period } = this.groupBy;
      // Example: 'Jul - 14 - Jul - 20'
      if (period === 'week') {
        const from = unixTimeStamp;
        const fromAsString = this.formatUnixToDate(from);
        const endOfWeek = sixDaysLater(from);
        const endOfWeekAsString = this.formatUnixToDate(endOfWeek);

        if (this.sizeY === 1 || this.sizeX === 1 || this.currentWidth < 600)
          this.primaryXAxis.labelStyle.size = '9px';
        return `${fromAsString} - ${endOfWeekAsString}`;
      }
      // Example: 'Jul'
      if (period === 'month')
        return this.formatUnixToDate(unixTimeStamp, 'month');
      // Example: '2024'
      if (period === 'year')
        return this.formatUnixToDate(unixTimeStamp, 'year');
      // Example: 'Jul - 14'
      return this.formatUnixToDate(unixTimeStamp);
    },
    generateUniqId() {
      return parseInt(Date.now() * Math.random().toFixed(5), 10);
    },

    logsss() {
      // if (this.timeSubmetric) {
      //   console.log(JSON.stringify(this.seriesDataForTimeSubmetrics));
      // } else console.log(JSON.stringify(this.seriesData));
      // console.log('timeSubmetric: ' + this.timeSubmetric);
      // console.log('intervalY: ' + this.intervalY);
      // console.log('maximumY: ' + this.maximumY);
    },
    tooltipRender(args) {
      // Se for média de tempo eu exibo uma mensagem explicando o cálculo
      if (!this.timeSubmetric) return;
      const index = args.point.index;
      const currentDate = this.seriesDataForTimeSubmetrics[index];
      const i18nKey = TRANSLATION_KEYS[this.data.submetric];
      const text = this.$t(`REPORT.METRICS.${i18nKey}.TOOLTIP_TEXT`, {
        metricValue: currentDate.value,
        conversationCount: currentDate.count,
      });
      args.text = text;
    },
    // Transforma os segundos na maior escala que couber 1 inteiro
    formattedTimeInBiggestRepresentation(seconds) {
      const timeRepres = this.currentTimeRepresentation;
      let time = seconds; // SECONDS
      if (timeRepres === 'min') time /= 60; // MINUTES
      if (timeRepres === 'h') time /= 3600; // HOURS
      if (timeRepres === 'd') time /= 86400; // HOURS
      return time;
    },
  },
};
</script>

<template>
  <div v-if="noDataFound" class="text-xs" @click="logsss">
    {{ $t('CUSTOM_DASHBOARDS.MESSAGES.NO_MESSAGES') }}
  </div>
  <div
    v-else
    class="control-section pt-25"
    :style="{ 'padding-top': responsiveSizes.containerPaddingTop }"
    @click="logsss"
  >
    <div align="center">
      <div v-if="groupByI18nKey" class="text-sm text-slate-800">
        {{
          $t(
            'CUSTOM_DASHBOARDS.MESSAGES.DISPLAYS.COLUMN_CHART.DATA_GROUPED_BY'
          ) + $t(groupByI18nKey)
        }}
      </div>
      <ChartComponent
        :id="`chartcontainer-${generateUniqId()}`"
        class="block-display"
        :theme="theme"
        align="center"
        title=""
        :primary-x-axis="primaryXAxis"
        :primary-y-axis="primaryYAxis"
        :chart-area="chartArea"
        :width="responsiveSizes.containerWidth"
        :height="responsiveSizes.containerHeight"
        :tooltip="tooltip"
        :tooltip-render="tooltipRender"
        :legend-settings="legend"
      >
        <SeriesCollectionDirective>
          <SeriesDirective
            v-if="timeSubmetric"
            :fill="totalColor"
            :data-source="seriesDataForTimeSubmetrics"
            type="Column"
            tooltip-mapping-name=""
            x-name="date"
            y-name="time"
            z-name="count"
            name=""
            width="2"
            column-width="1.0"
            :marker="marker"
            column-spacing="0.1"
            legend-shape="Circle"
          />
          <SeriesDirective
            v-else
            :fill="totalColor"
            :data-source="seriesData"
            type="Column"
            tooltip-mapping-name=""
            x-name="date"
            y-name="value"
            name="total"
            width="2"
            column-width="1.0"
            :marker="marker"
            column-spacing="0.1"
            legend-shape="Circle"
          />
        </SeriesCollectionDirective>
      </ChartComponent>
    </div>
  </div>
</template>

<style>
.block-display {
  display: block;
}
</style>
